import { useState, useMemo } from 'react';
import { useIntl } from '@atlassian/jira-intl';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { hasLocalStorageRequestedTrial } from '@atlassian/jira-issue-content-placeholder-template/src/controllers/confluence-requested-trial.tsx';
import messages from '../../confluence-dummy-link/messages.tsx';

type useHasRequestedTrialLozengeTextState = [string, () => void];
/**
 * This hooks manages the state of the lozenge text, which is variable depending on
 * if the user has requested a trial for Confluence in CFFE or not.
 */
export const useHasRequestedTrialLozengeText = (): useHasRequestedTrialLozengeTextState => {
	const { cloudId } = useTenantContext();
	const { formatMessage } = useIntl();

	const [hasRequestedTrial, setHasRequestedTrial] = useState(() =>
		hasLocalStorageRequestedTrial(cloudId),
	);
	const computeLozengeText = () =>
		hasRequestedTrial
			? formatMessage(messages.requestSentLozenge)
			: formatMessage(messages.requestToTryLozenge);

	const recomputeHasRequestedTrialLozengeText = () => {
		setHasRequestedTrial(hasLocalStorageRequestedTrial(cloudId));
	};

	// Memoize the lozenge text to avoid unnecessary computations on each render.
	// This will only recompute when hasRequestedTrial changes.
	const requestStatusLozengeText = useMemo(
		() => computeLozengeText(),

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[hasRequestedTrial, formatMessage],
	);

	return [requestStatusLozengeText, recomputeHasRequestedTrialLozengeText];
};
